/**
 * T055/T121: Prompt Card Component
 * Card display for a prompt with title, category, and tags
 */

import { forwardRef, type HTMLAttributes } from 'react'
import { Link } from '@tanstack/react-router'
import { FileText, Tag } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { PromptResponse, PromptContent } from '@/services/prompts'

interface PromptCardProps extends Omit<HTMLAttributes<HTMLAnchorElement>, 'children'> {
  prompt: PromptResponse
}

export const PromptCard = forwardRef<HTMLAnchorElement, PromptCardProps>(
  function PromptCard({ prompt, className, ...props }, ref) {
    return (
      <Link
        ref={ref}
        to="/prompts/$id"
        params={{ id: prompt.id }}
        className={cn('outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg', className)}
        role="listitem"
        aria-label={`${prompt.title}${prompt.is_template ? ', Template' : ''}`}
        {...props}
      >
        <Card className="h-full transition-colors hover:border-primary/50 hover:bg-muted/50 focus-visible:border-primary">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="line-clamp-2 text-base font-medium">
              {prompt.title}
            </CardTitle>
            {prompt.is_template && (
              <Badge variant="secondary" className="shrink-0">
                Template
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {prompt.category && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" />
              <span>{prompt.category.name}</span>
            </div>
          )}
          {prompt.tags && prompt.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {prompt.tags.slice(0, 3).map((tag) => (
                <Badge key={tag.id} variant="outline" className="text-xs">
                  <Tag className="mr-1 h-3 w-3" />
                  {tag.name}
                </Badge>
              ))}
              {prompt.tags.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{prompt.tags.length - 3}
                </Badge>
              )}
            </div>
          )}
          <p className="line-clamp-2 text-sm text-muted-foreground">
            {getPromptPreview(prompt.content)}
          </p>
        </CardContent>
      </Card>
    </Link>
  )
}
)

function getPromptPreview(content: PromptContent): string {
  if (content.type === 'text') {
    return content.text.slice(0, 150)
  }
  if (content.type === 'chat') {
    const firstMessage = content.messages[0]
    if (firstMessage) {
      return `${firstMessage.role}: ${firstMessage.content.slice(0, 120)}`
    }
  }
  return ''
}
